import React, {
  useContext, useEffect, useState,
} from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { Grid, Label } from 'semantic-ui-react';
import {
  PROJECT_CHAT_SESSIONS, TEAM_CHAT_SESSIONS, GLOBAL_CHAT_SESSIONS,
  CHAT_SESSION, CHAT_MESSAGE, SEND_CHAT_MESSAGE,
} from '../../queries/chat';
import DataContext from '../../contexts/DataContext';
import ReferencePanel from '../Projects/ProjectReport/ReferencePanel';
import SessionList from './SessionList';
import ChatWindow from './ChatWindow';
import ShareChatModal from './ShareChatModal';

const MESSAGE_PAGE_SIZE = 100;
// How often to re-check a still-generating reply. The mutation itself now
// returns as soon as the user's message (and an empty 'pending' placeholder
// for the assistant's reply) are saved — the actual RAG + Gemini call runs
// in the background, so this is what surfaces the finished answer instead of
// one long-hanging GraphQL request.
const REPLY_POLL_INTERVAL_MS = 3000;
// Keeps the session list and reference panel pinned in the viewport (rather
// than scrolling away) as the chat window's own content scrolls, each
// independently scrollable if it's taller than the viewport itself.
const STICKY_SIDEBAR_STYLE = {
  position: 'sticky', top: '1em', maxHeight: 'calc(100vh - 2em)', overflowY: 'auto',
};

// Per-scope wiring: which sessions to list, how a new session's first message
// is addressed, and the copy shown in the chat window.
const SCOPE_CONFIG = {
  project: {
    sessionsQuery: PROJECT_CHAT_SESSIONS,
    sessionsField: 'projectChatSessions',
    sessionsVariables: ({ projectId }) => ({ projectId }),
    newSessionInput: ({ projectId }) => ({ projectId }),
    emptyText: 'Ask a question about this project to get started.',
    placeholder: 'Ask a question about this project...',
  },
  team: {
    sessionsQuery: TEAM_CHAT_SESSIONS,
    sessionsField: 'teamChatSessions',
    sessionsVariables: ({ teamId }) => ({ teamId }),
    newSessionInput: ({ teamId }) => ({ teamId }),
    emptyText: 'Ask a question across all of your team\'s projects to get started.',
    placeholder: 'Ask a question about your team\'s projects...',
  },
  global: {
    sessionsQuery: GLOBAL_CHAT_SESSIONS,
    sessionsField: 'globalChatSessions',
    sessionsVariables: () => ({}),
    newSessionInput: () => ({ global: true }),
    emptyText: 'Ask a question across every team\'s projects and Dexter\'s expert network.',
    placeholder: 'Ask a question across all of Dexter...',
  },
};

const ownerName = (owner) => (owner
  ? [owner.firstName, owner.lastName].filter(Boolean).join(' ') || owner.email
  : 'a teammate');

// One chat UI for every scope:
// - scope 'project' (projectId): the project page's AI Chat tab.
// - scope 'team' (teamId) and 'global': the /ai-chat page.
// `sessionId` / `onSessionChange` optionally sync the open session with the URL.
// `sidebarHeader` renders at the top of the left column, above "New chat".
const AiChat = ({
  scope, projectId, teamId, scopeLabel, sessionId, onSessionChange, sidebarHeader,
}) => {
  const config = SCOPE_CONFIG[scope];
  const scopeIds = { projectId, teamId };
  const { user } = useContext(DataContext);
  const apolloClient = useApolloClient();
  const [activeSessionId, setActiveSessionIdState] = useState(sessionId || null);
  // Summary of the open session (owner, sharing). Loaded with its messages,
  // since a session opened by id may not be in the user's own session list.
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedReference, setSelectedReference] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [pollingMessageId, setPollingMessageId] = useState(null);

  const {
    data: sessionsData, loading: sessionsLoading, refetch: refetchSessions,
  } = useQuery(config.sessionsQuery, {
    variables: { ...config.sessionsVariables(scopeIds), limit: 50, offset: 0 },
    skip: (scope === 'project' && !projectId) || (scope === 'team' && !teamId),
    fetchPolicy: 'cache-and-network',
  });

  const setActiveSessionId = (id) => {
    setActiveSessionIdState(id);
    if (onSessionChange) onSessionChange(id);
  };

  // Follows URL changes made outside this component (e.g. browser back/forward).
  useEffect(() => {
    if (onSessionChange && (sessionId || null) !== activeSessionId) {
      setActiveSessionIdState(sessionId || null);
      setSelectedReference(null);
      setSendError(null);
      setPollingMessageId(null);
    }
  }, [sessionId]);

  // Fetched imperatively (not via useQuery) so this is a one-shot load with
  // no ongoing cache subscription. A live useQuery here would re-deliver its
  // result — recomputed from cache, but still reading its own stale, frozen
  // `messages` list, since a mutation/poll response never appends new
  // message refs into it — every time refetchSessions() (called after
  // sending, and again once polling completes) rewrites this same session's
  // summary fields (messageCount, lastMessageAt, title) elsewhere. That
  // reactive re-delivery would silently clobber the up-to-date local
  // `messages` state with the stale list, making a just-sent exchange
  // disappear from view (though it's already correctly saved server-side).
  useEffect(() => {
    let isCurrent = true;

    if (!activeSessionId) {
      setMessages([]);
      setActiveSession(null);
      return () => { isCurrent = false; };
    }

    setMessagesLoading(true);
    apolloClient.query({
      query: CHAT_SESSION,
      variables: { id: activeSessionId, messageLimit: MESSAGE_PAGE_SIZE },
      fetchPolicy: 'network-only',
    }).then(({ data }) => {
      if (!isCurrent) return;
      const { messages: loadedMessages, ...sessionSummary } = data?.chatSession || {};
      setMessages(loadedMessages || []);
      setActiveSession(data?.chatSession ? sessionSummary : null);
    }).catch((error) => {
      if (isCurrent) setSendError(error.message);
    }).finally(() => {
      if (isCurrent) setMessagesLoading(false);
    });
    return () => { isCurrent = false; };
  }, [activeSessionId, apolloClient]);

  const [sendChatMessageMutation, { loading: sending }] = useMutation(SEND_CHAT_MESSAGE);

  // Polls the one pending assistant reply until it leaves 'pending' — skip
  // (rather than an explicit stopPolling() call) is what actually stops the
  // polling once pollingMessageId is cleared back to null below.
  useQuery(CHAT_MESSAGE, {
    variables: { id: pollingMessageId },
    skip: !pollingMessageId,
    pollInterval: REPLY_POLL_INTERVAL_MS,
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      const polledMessage = result?.chatMessage;
      if (!polledMessage || polledMessage.status === 'pending') return;
      setMessages((prev) => prev.map(
        (message) => (message.id === polledMessage.id ? polledMessage : message),
      ));
      setPollingMessageId(null);
      refetchSessions();
    },
  });

  const sessions = sessionsData?.[config.sessionsField] || [];

  // pollingMessageId is only cleared here, on a genuine user-initiated
  // session switch — not in a [activeSessionId] effect, since handleSend
  // itself sets activeSessionId (for a brand-new chat's first message) in
  // the same tick as pollingMessageId, and such an effect would immediately
  // wipe the polling id back out before a single poll request could fire.
  const handleNewChat = () => {
    setActiveSessionId(null);
    setSelectedReference(null);
    setSendError(null);
    setPollingMessageId(null);
  };

  const handleSelectSession = (id) => {
    setActiveSessionId(id);
    setSelectedReference(null);
    setSendError(null);
    setPollingMessageId(null);
  };

  const handleSend = async (text) => {
    setSendError(null);

    // Show the prompt immediately rather than waiting on the AI's reply —
    // reconciled below with the server's real user message once it returns
    // (or removed again if the send fails).
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      role: 'user',
      text,
      createdAt: Date.now(),
      sentBy: user ? {
        id: user.id, firstName: user.firstName, lastName: user.lastName, email: user.email,
      } : null,
      references: [],
    };
    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const { data: result } = await sendChatMessageMutation({
        variables: {
          input: activeSessionId
            ? { sessionId: activeSessionId, text }
            : { ...config.newSessionInput(scopeIds), text },
        },
      });
      const payload = result.sendChatMessage;
      setMessages((prev) => [
        ...prev.filter((message) => message.id !== tempId),
        payload.userMessage,
        payload.assistantMessage,
      ]);
      if (!activeSessionId) {
        setActiveSessionId(payload.session.id);
        setActiveSession(payload.session);
      }
      if (payload.assistantMessage.status === 'pending') {
        setPollingMessageId(payload.assistantMessage.id);
      }
      refetchSessions();
    } catch (error) {
      setMessages((prev) => prev.filter((message) => message.id !== tempId));
      setSendError(error.message);
    }
  };

  if (scope === 'project' && !projectId) return null;

  // Team/global sessions shared with this user are read-only: only the owner
  // can send in them.
  const isOwnedScope = scope !== 'project';
  const readOnly = isOwnedScope && !!activeSession && !activeSession.isOwner;
  const header = isOwnedScope && (scopeLabel || activeSession?.isOwner) ? (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em',
    }}
    >
      {scopeLabel ? <Label basic color={scope === 'global' ? 'orange' : 'blue'}>{scopeLabel}</Label> : <span />}
      {activeSession?.isOwner && (
        <ShareChatModal
          session={activeSession}
          onShared={(updated) => setActiveSession((prev) => ({ ...prev, ...updated }))}
        />
      )}
    </div>
  ) : null;

  return (
    <Grid container stackable id="project-chat">
      <Grid.Column width={3}>
        <div style={STICKY_SIDEBAR_STYLE}>
          {sidebarHeader}
          <SessionList
            sessions={sessions}
            loading={sessionsLoading}
            activeSessionId={activeSessionId}
            onSelectSession={handleSelectSession}
            onNewChat={handleNewChat}
            groupByOwnership={isOwnedScope}
          />
        </div>
      </Grid.Column>
      <Grid.Column width={10}>
        <ChatWindow
          messages={messages}
          loading={messagesLoading}
          sending={sending || !!pollingMessageId}
          error={sendError}
          onSend={handleSend}
          onSelectReference={setSelectedReference}
          header={header}
          readOnly={readOnly}
          readOnlyNote={`Shared with you by ${ownerName(activeSession?.owner)}. You can read this chat, but only its owner can send messages.`}
          placeholder={config.placeholder}
          emptyText={config.emptyText}
        />
      </Grid.Column>
      <Grid.Column width={3}>
        <div style={STICKY_SIDEBAR_STYLE}>
          <ReferencePanel projectId={projectId} reference={selectedReference} showTeam={scope === 'global'} />
        </div>
      </Grid.Column>
    </Grid>
  );
};

export default AiChat;
